import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useCreateTask } from '../state/data/task';
import TaskForm from './task/TaskForm';
import {
  CUSTOM_TASK_TEMPLATE_CODE, ASSIGNABLE_ROLES, mintManualDedupeKey, dueDateToIso,
} from './task/constants';

// Patient-scoped "create a custom task" modal opened from the patient file.
// The patient + care period are already resolved by the shell, so unlike the
// roster CreateTaskDrawer there's no patient picker / care-period fetch here.
export default function CreateTaskModal({
  patient, carePeriod, currentUser, userRole, onClose, onSuccess,
}) {
  const createTaskMut = useCreateTask();
  const defaultRole = ASSIGNABLE_ROLES.includes(userRole) ? userRole : 'care_coordinator';
  const [draft, setDraft] = useState({
    title: '',
    description: '',
    priority: 'normal',
    clinical_domain: '',
    owner_role: defaultRole,
    task_type: 'action',
    due_at: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dedupeKey] = useState(mintManualDedupeKey);

  // Escape key + body scroll lock (modal pattern).
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  function update(field, value) { setDraft((prev) => ({ ...prev, [field]: value })); }

  async function handleCreate() {
    if (!draft.title.trim()) { setError('Title is required'); return; }
    const carePeriodId = carePeriod?.care_period_id;
    if (!carePeriodId) { setError('No active care period found for this patient'); return; }
    setSaving(true);
    setError(null);
    try {
      const createdBy = currentUser ? {
        user_id: currentUser.accountid != null ? String(currentUser.accountid) : null,
        firstname: currentUser.firstname || currentUser.shortname || null,
        lastname: currentUser.lastname || null,
      } : undefined;
      const created = await createTaskMut.mutateAsync({
        ...draft,
        withings_user_id: String(patient.userid),
        care_period_id: carePeriodId,
        due_at: dueDateToIso(draft.due_at),
        template_code: CUSTOM_TASK_TEMPLATE_CODE,
        created_by: createdBy,
        dedupe_key: dedupeKey,
      });
      onSuccess?.(created);
    } catch (e) {
      setError(e?.message || 'Could not create task');
    } finally {
      setSaving(false);
    }
  }

  const patientName = [patient?.firstname, patient?.lastname].filter(Boolean).join(' ');

  return createPortal((
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <p className="eyebrow text-secondary" style={{ margin: '0 0 4px' }}>New task</p>
            <h2 className="header3" style={{ margin: 0 }}>
              {patientName ? `Create a task for ${patientName}` : 'Create a custom task'}
            </h2>
          </div>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, margin: '16px 0' }}>
          <TaskForm draft={draft} onChange={update} />
          {error && (
            <p className="detail1" style={{ color: 'var(--wi-status-bad)', margin: 0 }}>{error}</p>
          )}
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleCreate} disabled={saving} style={{ minWidth: 110 }}>
            {saving ? 'Creating…' : 'Create task'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
